import { Link } from "react-router-dom";
import { ArrowRight, BookOpen, ShieldCheck, Baby, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Hero = () => {
  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative pt-28 sm:pt-32 pb-16 sm:pb-24 overflow-hidden medical-grid"
    >
      {/* Decorative blobs */}
      <div className="pointer-events-none absolute -top-24 -right-24 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
      <div className="pointer-events-none absolute top-40 -left-32 w-96 h-96 bg-accent/20 rounded-full blur-3xl" />

      <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16 items-center relative">
        <div className="lg:col-span-7 animate-fade-up">
          <div className="inline-flex items-center gap-2 bg-secondary text-secondary-foreground px-4 py-1.5 rounded-full text-sm font-medium mb-6 border border-primary/15">
            <Sparkles className="w-4 h-4 text-primary" />
            Pediatric Wellness · Pediarange Series Launched
          </div>

          <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-foreground">
            Caring for
            <span className="gradient-text-blue"> little ones, </span>
            <br className="hidden sm:block" />
            trusted by physicians.
          </h1>

          <p className="mt-6 text-base sm:text-lg leading-relaxed text-muted-foreground max-w-xl">
            Ayushman Distributors brings you the <strong className="text-foreground">Pediarange</strong> — a
            specialised pediatric portfolio of vitamin drops, gut-health probiotics
            and growth supplements crafted for healthy children and confident parents.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#products" data-testid="hero-explore-products-btn">
              <Button
                size="lg"
                className="rounded-full bg-primary hover:bg-primary/90 px-7 h-12 text-base"
              >
                Explore Pediarange
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </a>
            <Link to="/catalogue" data-testid="hero-open-catalogue-btn">
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-7 h-12 text-base border-2 border-primary/30 hover:border-primary hover:bg-secondary"
              >
                <BookOpen className="mr-2 w-4 h-4" />
                Open Digital Catalogue
              </Button>
            </Link>
          </div>

          <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg">
            {[
              { icon: Baby, label: "Pediatric Focus" },
              { icon: ShieldCheck, label: "Quality Assured" },
              { icon: BookOpen, label: "MR Friendly" },
            ].map(({ icon: Icon, label }) => (
              <div
                key={label}
                className="bg-white border border-border rounded-2xl p-4 text-center soft-shadow"
              >
                <Icon className="w-5 h-5 text-primary mx-auto mb-2" />
                <p className="text-xs sm:text-sm font-medium text-foreground/80">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>

        <div className="lg:col-span-5 relative animate-fade-up" style={{ animationDelay: "0.2s" }}>
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-primary/15 to-accent/20 rounded-[40px] blur-2xl" />
            <div className="relative bg-white rounded-[36px] border border-border p-6 sm:p-8 soft-shadow">
              <div className="grid grid-cols-2 gap-3">
                <div className="aspect-square rounded-2xl bg-gradient-to-br from-primary to-[#2B7FE3] p-5 text-white flex flex-col justify-between">
                  <Baby className="w-7 h-7" />
                  <div>
                    <p className="text-2xl font-heading font-bold">3</p>
                    <p className="text-xs opacity-90">Launched products</p>
                  </div>
                </div>
                <div className="aspect-square rounded-2xl bg-secondary p-5 flex flex-col justify-between">
                  <ShieldCheck className="w-7 h-7 text-primary" />
                  <div>
                    <p className="text-2xl font-heading font-bold text-primary">100%</p>
                    <p className="text-xs text-secondary-foreground/80">
                      Quality compliant
                    </p>
                  </div>
                </div>
                <div className="aspect-square rounded-2xl bg-accent p-5 flex flex-col justify-between text-accent-foreground">
                  <Sparkles className="w-7 h-7" />
                  <div>
                    <p className="text-2xl font-heading font-bold">Pediatrics</p>
                    <p className="text-xs">Specialised segment</p>
                  </div>
                </div>
                <div className="aspect-square rounded-2xl bg-foreground p-5 text-background flex flex-col justify-between">
                  <BookOpen className="w-7 h-7" />
                  <div>
                    <p className="text-lg font-heading font-bold">Digital Catalogue</p>
                    <p className="text-xs opacity-80">Tablet ready</p>
                  </div>
                </div>
              </div>
              <div className="mt-5 p-4 bg-secondary rounded-2xl flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-primary text-primary-foreground grid place-items-center font-bold">
                  Rx
                </div>
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Recommended by paediatricians
                  </p>
                  <p className="text-xs text-muted-foreground">
                    Designed for growing children
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
